import { useState } from "react";

import type { SelfDto } from "@/api/types";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { Copy01Icon } from "@/components/Icons/Copy01Icon";
import { Share01Icon } from "@/components/Icons/Share01Icon";
import { Modal } from "@/components/Modal/Modal";
import { Button } from "@/design-system/Button";
import { IconButton } from "@/design-system/IconButton";
import { Input } from "@/design-system/Input";
import { Spread } from "@/design-system/Spread";
import { Stack } from "@/design-system/Stack";
import { Switch } from "@/design-system/Switch";
import { Text } from "@/design-system/Text";
import { useProjectAppBrandingConfig } from "@/providers/ProjectAppBrandingConfigProvider";
import { selfAccountMutations } from "@/queries/self-account/mutations";
import { userMutations } from "@/queries/users/mutations";
import { useTranslation } from "@/translations";
import { QrCode } from "@/ui-adapters/ArkQrCode";

type ResidentUserMenuModalsProps = {
  sessionUser: SelfDto;
  isPrivacySettingsOpen: boolean;
  isHousemateInviteOpen: boolean;
  onPrivacySettingsOpenChange: (isOpen: boolean) => void;
  onHousemateInviteOpenChange: (isOpen: boolean) => void;
};

export function ResidentUserMenuModals({
  sessionUser,
  isPrivacySettingsOpen,
  isHousemateInviteOpen,
  onPrivacySettingsOpenChange,
  onHousemateInviteOpenChange,
}: ResidentUserMenuModalsProps): React.ReactNode {
  const isCompanyBased = sessionUser.project.type === "companyBased";
  const canInviteResident = isCompanyBased ? sessionUser.company !== undefined : sessionUser.address !== undefined;
  const companyName = sessionUser.company?.name;

  return (
    <>
      {isPrivacySettingsOpen && (
        <PrivacySettingsModal sessionUser={sessionUser} isOpened onOpenChange={onPrivacySettingsOpenChange} />
      )}
      {canInviteResident && isHousemateInviteOpen && (
        <ResidentInviteModal
          companyName={companyName}
          isCompanyBased={isCompanyBased}
          isOpened
          onOpenChange={onHousemateInviteOpenChange}
        />
      )}
    </>
  );
}

type PrivacySettingsModalProps = {
  sessionUser: SelfDto;
  isOpened: boolean;
  onOpenChange: (isOpen: boolean) => void;
};

function PrivacySettingsModal({ sessionUser, isOpened, onOpenChange }: PrivacySettingsModalProps): React.ReactNode {
  const { t } = useTranslation();
  const showFlashToast = useFlashToast();
  const updatePrivacySettings = selfAccountMutations.useUpdatePrivacySettings();
  const [settings, setSettings] = useState({
    showEmail: !sessionUser.emailHidden,
    showPhoneNumber: !sessionUser.phoneNumberHidden,
    showHouseNumber: !sessionUser.hideHouseNumber,
    enableChat: sessionUser.chatEnabled,
  });

  const updateSetting = (key: keyof typeof settings, value: boolean) => {
    setSettings((current) => ({ ...current, [key]: value }));
  };

  const handleSave = async () => {
    try {
      await updatePrivacySettings.mutateAsync({
        hideEmail: !settings.showEmail,
        hidePhoneNumber: !settings.showPhoneNumber,
        hideHouseNumber: !settings.showHouseNumber,
        enableChat: settings.enableChat,
      });
      showFlashToast({
        variant: "success",
        message: t("component.user-menu.privacy-settings.saved"),
      });
      onOpenChange(false);
    } catch {
      showFlashToast({
        variant: "alert",
        message: t("component.user-menu.privacy-settings.error"),
      });
    }
  };

  return (
    <Modal.Root
      isOpened={isOpened}
      onOpenChange={onOpenChange}
      title={t("component.user-menu.privacy-settings")}
      description={t("component.user-menu.privacy-settings.description")}
      size="sm"
    >
      <Modal.Body>
        <Stack gap="md">
          <PrivacySetting
            label={t("component.user-menu.privacy-settings.email")}
            isChecked={settings.showEmail}
            disabled={updatePrivacySettings.isPending}
            onChange={(isChecked) => updateSetting("showEmail", isChecked)}
          />
          <PrivacySetting
            label={t("component.user-menu.privacy-settings.phone-number")}
            isChecked={settings.showPhoneNumber}
            disabled={updatePrivacySettings.isPending}
            onChange={(isChecked) => updateSetting("showPhoneNumber", isChecked)}
          />
          <PrivacySetting
            label={t("component.user-menu.privacy-settings.house-number")}
            isChecked={settings.showHouseNumber}
            disabled={updatePrivacySettings.isPending}
            onChange={(isChecked) => updateSetting("showHouseNumber", isChecked)}
          />
          <PrivacySetting
            label={t("component.user-menu.privacy-settings.chat")}
            isChecked={settings.enableChat}
            disabled={updatePrivacySettings.isPending}
            onChange={(isChecked) => updateSetting("enableChat", isChecked)}
          />
        </Stack>
      </Modal.Body>
      <Modal.Actions>
        <Modal.Close>
          <Button variant="outline" disabled={updatePrivacySettings.isPending}>
            {t("common.action.cancel")}
          </Button>
        </Modal.Close>
        <Button onClick={() => void handleSave()} isLoading={updatePrivacySettings.isPending}>
          {t("common.action.save")}
        </Button>
      </Modal.Actions>
    </Modal.Root>
  );
}

type PrivacySettingProps = {
  label: string;
  isChecked: boolean;
  disabled: boolean;
  onChange: (isChecked: boolean) => void;
};

function PrivacySetting({ label, isChecked, disabled, onChange }: PrivacySettingProps): React.ReactNode {
  return (
    <Spread space="md">
      <span>{label}</span>
      <Switch
        ariaLabel={label}
        isChecked={isChecked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
      />
    </Spread>
  );
}

type ResidentInviteModalProps = {
  companyName?: string;
  isCompanyBased: boolean;
  isOpened: boolean;
  onOpenChange: (isOpen: boolean) => void;
};

function ResidentInviteModal({
  companyName,
  isCompanyBased,
  isOpened,
  onOpenChange,
}: ResidentInviteModalProps): React.ReactNode {
  const { t } = useTranslation();
  const showFlashToast = useFlashToast();
  const createHousemateInvite = userMutations.useCreateHousemateInvite();
  const getRegistrationCode = selfAccountMutations.useGetRegistrationCode();
  const { appBrandingConfig } = useProjectAppBrandingConfig();
  const [inviteLink, setInviteLink] = useState<string>();
  const canShare = "share" in navigator;

  const createInviteLink = async () => {
    try {
      const code = isCompanyBased
        ? await getRegistrationCode.mutateAsync()
        : (await createHousemateInvite.mutateAsync()).inviteCode;
      const link = new URL("/invite-code", appBrandingConfig.deepLink);
      link.searchParams.set("t", "1");
      link.searchParams.set("code", code);
      setInviteLink(link.toString());
    } catch {
      showFlashToast({
        variant: "alert",
        message: t("component.user-menu.invite-housemate.error"),
      });
    }
  };

  const copyInviteLink = async () => {
    if (!inviteLink) {
      return;
    }

    try {
      await navigator.clipboard.writeText(inviteLink);
      showFlashToast({
        variant: "success",
        message: t("component.user-menu.invite-housemate.copied"),
      });
    } catch {
      showFlashToast({
        variant: "alert",
        message: t("component.user-menu.invite-housemate.copy-error"),
      });
    }
  };

  const shareInviteLink = async () => {
    if (!inviteLink || !navigator.share) {
      return;
    }

    try {
      const companyShareText = t("view.share_code.join_colleague.text")
        .replace("${address}", companyName ?? "")
        .replace("${brand}", appBrandingConfig.appName);

      await navigator.share({
        title: isCompanyBased ? t("view.share_code.company.main_title") : t("component.user-menu.invite-housemate"),
        text: isCompanyBased
          ? companyShareText
          : t("component.user-menu.invite-housemate.share-text", { appName: appBrandingConfig.appName }),
        url: inviteLink,
      });
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        return;
      }

      showFlashToast({
        variant: "alert",
        message: t("component.user-menu.invite-housemate.share-error"),
      });
    }
  };

  return (
    <Modal.Root
      isOpened={isOpened}
      onOpenChange={onOpenChange}
      title={isCompanyBased ? t("view.share_code.company.main_title") : t("component.user-menu.invite-housemate")}
      description={
        isCompanyBased
          ? t("view.share_code.company.description1.subtitle")
          : t("component.user-menu.invite-housemate.description")
      }
      descriptionTone="primary"
      size="sm"
    >
      <Modal.Body>
        {inviteLink ? (
          <Stack gap="md">
            <QrCode.Root
              value={inviteLink}
              encoding={{ ecc: "M" }}
              className="aspect-square w-full max-w-compact-preview self-center rounded-lg border border-border-default bg-background-default p-space-sm text-text-primary"
            >
              <QrCode.Frame
                role="img"
                aria-label={
                  isCompanyBased
                    ? t("view.share_code.company.description3.title")
                    : t("component.user-menu.invite-housemate.link")
                }
                className="size-full fill-current"
              >
                <QrCode.Pattern />
              </QrCode.Frame>
            </QrCode.Root>
            <Stack gap="sm">
              <Text as="label" htmlFor="housemate-invite-link" variant="body-emphasis">
                {isCompanyBased
                  ? t("view.share_code.company.description3.title")
                  : t("component.user-menu.invite-housemate.link")}
              </Text>
              <Input
                id="housemate-invite-link"
                width="full"
                value={inviteLink}
                readOnly
                readOnlyTextTone="primary"
                postfix={
                  <IconButton
                    icon={<Copy01Icon />}
                    title={t("component.user-menu.invite-housemate.copy")}
                    variant="ghostSecondary"
                    size="sm"
                    onClick={() => void copyInviteLink()}
                  />
                }
              />
            </Stack>
          </Stack>
        ) : (
          <Text as="p">
            {isCompanyBased
              ? t("view.share_code.company.description3.subtitle")
              : t("component.user-menu.invite-housemate.expires")}
          </Text>
        )}
      </Modal.Body>
      {(!inviteLink || canShare) && (
        <Modal.Actions>
          {inviteLink ? (
            <Button icon={<Share01Icon />} onClick={() => void shareInviteLink()}>
              {t("component.user-menu.invite-housemate.share")}
            </Button>
          ) : (
            <Button
              onClick={() => void createInviteLink()}
              isLoading={createHousemateInvite.isPending || getRegistrationCode.isPending}
            >
              {isCompanyBased
                ? t("view.share_code.company.button.invite.title")
                : t("component.user-menu.invite-housemate.create")}
            </Button>
          )}
        </Modal.Actions>
      )}
    </Modal.Root>
  );
}
